import React, { useMemo } from 'react';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { ProgressCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/ProgressDefinition';
import { ProgressPageInterface } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageInterface';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { Helmet } from 'react-helmet';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { isEntitledToProgress } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { ProgressBarChartWithDates } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChartWithDates';
import { TimeSeriesTrendsCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsDefinition';
import { isProgressEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isTimeSeriesTrendsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { useParams } from 'react-router';

export const ProgressPage = () => {
  const language = useLanguage();
  const { myObservation, selectedOrganizationId } = useObservation();

  useAuthenticatedPageRedirect();

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation();

  const progressEnabled = useMemo(() => {
    return (
      isProgressEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToProgress({
        features: myObservation?.features ?? [],
        focusArea: null,
      })
    );
  }, [myObservation]);

  const navigate = useNavigateSafe();

  if (myObservation && !progressEnabled) {
    navigate('/overview');
  }

  const { sectionName: sectionNameRaw } = useParams();

  const showMetricsTrends = useMemo(
    () =>
      isTimeSeriesTrendsEnabledForRevision({ revisionFlags: myObservation }),
    [myObservation],
  );

  const activeTab = useMemo(() => {
    if (sectionNameRaw === 'trends' && showMetricsTrends) {
      return 'trends';
    }

    return 'disparities';
  }, [sectionNameRaw, showMetricsTrends]);

  return (
    <MainLayout showBackToTop={isLoadingStill}>
      <Helmet>
        <title>
          {documentTitle(getTranslatedContentAsString(language.progress()))}
        </title>
      </Helmet>

      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isLoadingStill}
        failedToLoadObservation={failedToLoadObservation}
      />

      <PageSection>
        <Spacing direction="vertical" size="md">
          <div className="mt-0" data-testid="page-title">
            <S2Typography
              variant="headline-3"
              translatedContent={language.progress()}
            />
          </div>
          {/* Tabs */}
          <div className="mb-4 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'disparities'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => {
                  if (activeTab !== 'disparities') {
                    navigate('/progress/disparities');
                  }
                }}
                type="button"
              >
                {language.disparityTrends()}
              </button>
              {showMetricsTrends && (
                <button
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'trends'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    if (activeTab !== 'trends') {
                      navigate('/progress/trends');
                    }
                  }}
                  type="button"
                >
                  {language.timeSeriesTrends()}
                </button>
              )}
            </nav>
          </div>
        </Spacing>
      </PageSection>

      {/* Tab content */}
      {activeTab === 'disparities' &&
        !isLoadingStill &&
        myObservation &&
        progressEnabled && (
          <ProgressView
            myObservation={myObservation}
            organizationId={selectedOrganizationId}
          />
        )}

      {activeTab === 'trends' && (
        <ProgressFilteredView
          myObservation={myObservation}
          organizationId={selectedOrganizationId}
        />
      )}
    </MainLayout>
  );
};

const ProgressView = ({
  myObservation,
  organizationId,
}: {
  organizationId: string | null;
  myObservation: MyObservation | null;
}) => {
  const { userAuthenticatedApiCall } = useUser();

  if (!myObservation || !organizationId) {
    return null;
  }

  return (
    <CustomViz
      observation={myObservation.type}
      organizationId={organizationId}
      customVizDefinition={ProgressCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <PageSection>
        <ProgressPageInterface
          myObservation={myObservation}
          organizationId={organizationId}
        />
      </PageSection>
    </CustomViz>
  );
};

const ProgressFilteredView = ({
  myObservation,
  organizationId,
}: {
  organizationId: string | null;
  myObservation: MyObservation | null;
}) => {
  const { userAuthenticatedApiCall } = useUser();

  if (!myObservation || !organizationId) {
    return null;
  }

  return (
    <CustomViz
      observation={myObservation.type}
      organizationId={organizationId}
      customVizDefinition={TimeSeriesTrendsCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <PageSection>
        <ProgressBarChartWithDates />
      </PageSection>
    </CustomViz>
  );
};
